import React from 'react';
import { AccountGroup, AccountGroupColor, AccountGroupRef, AccountGroupSkipped } from '../../types';
import { cx, FOCUS_RING } from './styles';

/**
 * The one place an account group gets a colour. Violet is deliberately absent:
 * in this product it means CrowdVolt and nothing else (see Badge.tsx).
 */
export const GROUP_COLORS: ReadonlyArray<{
  value: AccountGroupColor;
  label: string;
  chip: string;
  dot: string;
}> = [
  { value: '', label: 'Grey', chip: 'bg-white/[0.06] text-white/70 border-white/15', dot: 'bg-white/40' },
  { value: 'rose', label: 'Rose', chip: 'bg-rose-500/10 text-rose-300 border-rose-500/30', dot: 'bg-rose-400' },
  { value: 'amber', label: 'Amber', chip: 'bg-amber-500/10 text-amber-300 border-amber-500/30', dot: 'bg-amber-400' },
  { value: 'emerald', label: 'Green', chip: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-400' },
  { value: 'sky', label: 'Sky', chip: 'bg-sky-500/10 text-sky-300 border-sky-500/30', dot: 'bg-sky-400' },
  { value: 'teal', label: 'Teal', chip: 'bg-teal-500/10 text-teal-300 border-teal-500/30', dot: 'bg-teal-400' },
  { value: 'indigo', label: 'Indigo', chip: 'bg-indigo-500/10 text-indigo-300 border-indigo-500/30', dot: 'bg-indigo-400' },
  { value: 'fuchsia', label: 'Pink', chip: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30', dot: 'bg-fuchsia-400' },
  { value: 'lime', label: 'Lime', chip: 'bg-lime-500/10 text-lime-300 border-lime-500/30', dot: 'bg-lime-400' },
  { value: 'orange', label: 'Orange', chip: 'bg-orange-500/10 text-orange-300 border-orange-500/30', dot: 'bg-orange-400' },
];

const colorEntry = (color: string | undefined) =>
  GROUP_COLORS.find((c) => c.value === (color ?? '')) ?? GROUP_COLORS[0];

export const groupChipClass = (color: string | undefined) => colorEntry(color).chip;
export const groupDotClass = (color: string | undefined) => colorEntry(color).dot;

/**
 * "8 of 10 eligible" plus why — the sentence every group action prints, so the
 * seller is never surprised that an AXS or banned account was left out.
 */
export const eligibilityLabel = (
  eligible: number,
  total: number,
  skipped?: AccountGroupSkipped,
): string => {
  if (total === 0) return 'No accounts';
  const head = eligible === total
    ? `${eligible} eligible`
    : `${eligible} of ${total} eligible`;
  if (!skipped || eligible === total) return head;
  const why = [
    skipped.non_dice > 0 ? `${skipped.non_dice} not DICE` : null,
    skipped.banned > 0 ? `${skipped.banned} banned` : null,
    skipped.inactive > 0 ? `${skipped.inactive} inactive` : null,
  ].filter(Boolean);
  return why.length > 0 ? `${head} (${why.join(', ')})` : head;
};

/** The standing rule, as a short readable phrase. Empty for a plain label. */
export const ruleLabel = (group: Pick<AccountGroup, 'flash_buy_enabled' | 'tier_match'>): string => {
  const parts: string[] = [];
  if (group.flash_buy_enabled === true) parts.push('Flash Buy on');
  if (group.flash_buy_enabled === false) parts.push('Flash Buy off');
  if (group.tier_match === 'match') parts.push('Match Tier');
  if (group.tier_match === 'ga') parts.push('GA');
  if (group.tier_match === 'vip') parts.push('VIP');
  return parts.join(' · ');
};

export interface AccountGroupChipProps {
  group: AccountGroupRef;
  size?: 'sm' | 'md';
  /** Renders a small × that removes the tag. Owner-only surfaces pass this. */
  onRemove?: () => void;
  onClick?: () => void;
  /** Selected state for filter chips. */
  active?: boolean;
  title?: string;
  /** Trailing count for filter chips. */
  count?: number;
  disabled?: boolean;
}

export const AccountGroupChip: React.FC<AccountGroupChipProps> = ({
  group,
  size = 'sm',
  onRemove,
  onClick,
  active,
  title,
  count,
  disabled,
}) => {
  const colour = colorEntry(group.color);
  const base = cx(
    'inline-flex max-w-full items-center gap-1.5 rounded-full border font-medium whitespace-nowrap',
    size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs',
    // A filter chip that is NOT selected reads as neutral so the selected one stands out.
    onClick && active === false
      ? 'border-white/10 bg-white/5 text-white/50 hover:bg-white/10'
      : colour.chip,
  );

  const body = (
    <>
      <span className={cx('h-1.5 w-1.5 flex-shrink-0 rounded-full', colour.dot)} />
      <span className="truncate">{group.name}</span>
      {count != null && (
        <span className="tabular-nums opacity-60">{count}</span>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        title={title}
        disabled={disabled}
        className={cx(base, 'transition-all disabled:opacity-50', FOCUS_RING)}
      >
        {body}
      </button>
    );
  }

  return (
    <span className={base} title={title}>
      {body}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove from ${group.name}`}
          title={`Remove from ${group.name}`}
          className={cx(
            '-mr-1 ml-0.5 rounded-full p-0.5 opacity-60 transition-opacity hover:opacity-100 disabled:opacity-30',
            FOCUS_RING,
          )}
        >
          <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </span>
  );
};
